// src/components/common/ErrorModal.tsx
/**
 * A styled error/alert modal that replaces browser alerts
 * Matches the existing app design language
 */

import React from 'react';
import { AlertCircle, XCircle, CheckCircle, Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatErrorForUser, getErrorSuggestion } from '@/utils/errorUtils';

export type ModalType = 'error' | 'warning' | 'success' | 'info';

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  /** Optional suggestion text to display below the main message */
  suggestion?: string | null;
  type?: ModalType;
  actionLabel?: string;
  onAction?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  /** If true, will auto-format the message using errorUtils */
  autoFormat?: boolean;
}

const modalConfig: Record<ModalType, {
  icon: React.ElementType;
  iconColor: string;
  borderColor: string;
  bgGradient: string;
  buttonColor: string;
}> = {
  error: {
    icon: XCircle,
    iconColor: 'text-red-500 dark:text-red-400',
    borderColor: 'border-red-400 dark:border-red-500',
    bgGradient: 'from-red-50 to-white dark:from-red-900/20 dark:to-gray-900',
    buttonColor: 'bg-red-600 hover:bg-red-700'
  },
  warning: {
    icon: AlertCircle,
    iconColor: 'text-amber-500 dark:text-amber-400',
    borderColor: 'border-amber-400 dark:border-amber-500',
    bgGradient: 'from-amber-50 to-white dark:from-amber-900/20 dark:to-gray-900',
    buttonColor: 'bg-amber-600 hover:bg-amber-700'
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-green-500 dark:text-green-400',
    borderColor: 'border-green-400 dark:border-green-500',
    bgGradient: 'from-green-50 to-white dark:from-green-900/20 dark:to-gray-900',
    buttonColor: 'bg-green-600 hover:bg-green-700'
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-500 dark:text-blue-400',
    borderColor: 'border-blue-400 dark:border-blue-500',
    bgGradient: 'from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-900',
    buttonColor: 'bg-blue-600 hover:bg-blue-700'
  }
};

export const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  suggestion,
  type = 'error',
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  autoFormat = true
}) => {
  if (!isOpen) return null;

  const config = modalConfig[type];
  const Icon = config.icon;
  
  const defaultTitles: Record<ModalType, string> = {
    error: 'An Error Occurred',
    warning: 'Warning',
    success: 'Success',
    info: 'Information'
  };

  const displayTitle = title || defaultTitles[type];
  
  // Auto-format the message if enabled (for error type by default)
  const displayMessage = autoFormat && type === 'error' 
    ? formatErrorForUser(message) 
    : message;
  
  // Get suggestion if not provided and auto-format is enabled
  const displaySuggestion = suggestion !== undefined 
    ? suggestion 
    : (autoFormat && type === 'error' ? getErrorSuggestion(message) : null);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`
        relative w-full max-w-md
        bg-gradient-to-b ${config.bgGradient}
        border ${config.borderColor}
        rounded-2xl shadow-2xl
        p-6
        animate-in fade-in-0 zoom-in-95 duration-200
      `}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg`}>
            <Icon className={`h-8 w-8 ${config.iconColor}`} />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-3">
          {displayTitle}
        </h2>

        {/* Message */}
        <div className="mb-6 max-h-48 overflow-y-auto">
          <p className="text-gray-700 dark:text-gray-300 text-center leading-relaxed">
            {displayMessage}
          </p>
          {displaySuggestion && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-3 italic">
              💡 {displaySuggestion}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {onAction && actionLabel ? (
            <Button
              onClick={() => {
                onAction();
                onClose();
              }}
              className={`w-full ${config.buttonColor} text-white font-semibold py-2.5 rounded-xl`}
            >
              {actionLabel}
            </Button>
          ) : null}
          
          {onSecondary && secondaryLabel ? (
            <Button
              variant="outline"
              onClick={() => {
                onSecondary();
                onClose();
              }}
              className="w-full border-gray-300 dark:border-gray-600 font-semibold py-2.5 rounded-xl"
            >
              {secondaryLabel}
            </Button>
          ) : null}
          
          {/* Default close button if no other actions */}
          {!onAction && !onSecondary && (
            <Button
              onClick={onClose}
              className={`w-full ${config.buttonColor} text-white font-semibold py-2.5 rounded-xl`}
            >
              OK
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook for easier usage
interface UseErrorModalReturn {
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  modalProps: ErrorModalProps;
}

export function useErrorModal(): UseErrorModalReturn {
  const [isOpen, setIsOpen] = React.useState(false);
  const [modalState, setModalState] = React.useState<{
    message: string;
    title?: string;
    type: ModalType;
  }>({
    message: '',
    type: 'error'
  });

  const showModal = (type: ModalType, message: string, title?: string) => {
    setModalState({ message, title, type });
    setIsOpen(true);
  };

  return {
    showError: (message, title) => showModal('error', message, title),
    showWarning: (message, title) => showModal('warning', message, title),
    showSuccess: (message, title) => showModal('success', message, title),
    showInfo: (message, title) => showModal('info', message, title),
    modalProps: {
      isOpen,
      onClose: () => setIsOpen(false),
      message: modalState.message,
      title: modalState.title,
      type: modalState.type
    }
  };
}

export default ErrorModal;
